import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { usersAPI } from '../lib/api';
import { toast } from 'sonner';
import {
    Users,
    UserPlus,
    Edit2,
    Trash2,
    Key,
    Shield,
    User as UserIcon,
    X,
    Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';

export function UserManagementPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { user: currentUser } = useAuth();

    // Form states
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'user'
    });
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await usersAPI.getAll();
            setUsers(response.data.users);
        } catch (error) {
            toast.error('Gagal memuat daftar user');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        if (!formData.username || !formData.password) {
            toast.error('Username dan password harus diisi');
            return;
        }

        try {
            const response = await usersAPI.create(formData.username, formData.password, formData.role);
            if (response.data.success) {
                toast.success(
                    <div className="flex flex-col gap-1">
                        <div className="font-semibold">✅ User Berhasil Dibuat!</div>
                        <div className="text-sm">
                            Username: <span className="font-medium">{formData.username}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Role: {formData.role}
                        </div>
                    </div>,
                    { duration: 4000 }
                );
                setShowCreateDialog(false);
                setFormData({ username: '', password: '', role: 'user' });
                loadUsers();
            }
        } catch (error) {
            toast.error(
                <div className="flex flex-col gap-1">
                    <div className="font-semibold">❌ Gagal Membuat User</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {error.response?.data?.message || 'Terjadi kesalahan'}
                    </div>
                </div>,
                { duration: 5000 }
            );
        }
    };

    const handleUpdateRole = async () => {
        if (!selectedUser) return;

        try {
            const response = await usersAPI.update(selectedUser.id, { role: formData.role });
            if (response.data.success) {
                toast.success('Role user berhasil diperbarui');
                setShowEditDialog(false);
                loadUsers();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal memperbarui role');
        }
    };

    const handleChangePassword = async () => {
        if (!selectedUser || !newPassword) {
            toast.error('Password baru harus diisi');
            return;
        }

        try {
            const response = await usersAPI.changePassword(selectedUser.id, newPassword);
            if (response.data.success) {
                toast.success('Password berhasil diubah');
                setShowPasswordDialog(false);
                setNewPassword('');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal mengubah password');
        }
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Apakah Anda yakin ingin menghapus user "${user.username}"?`)) {
            return;
        }

        try {
            const response = await usersAPI.delete(user.id);
            if (response.data.success) {
                toast.success('User berhasil dihapus');
                loadUsers();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal menghapus user');
        }
    };

    const openEditDialog = (user) => {
        setSelectedUser(user);
        setFormData({ ...formData, role: user.role });
        setShowEditDialog(true);
    };

    const openPasswordDialog = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowPasswordDialog(true);
    };

    if (loading) {
        return <div className="p-8 text-center">Memuat data user...</div>;
    }

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
        >
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Manajemen User</h2>
                    <p className="text-muted-foreground mt-1">
                        Kelola user dan hak akses sistem
                    </p>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Tambah User
                </Button>
            </div>

            {/* Users List */}
            <Card>
                <CardHeader>
                    <CardTitle>Daftar User</CardTitle>
                    <CardDescription>
                        Total {users.length} user terdaftar
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {users.map((user) => (
                            <div
                                key={user.id}
                                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`p-2 rounded-full mt-1 ${user.role === 'admin' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {user.role === 'admin' ? <Shield className="h-5 w-5" /> : <UserIcon className="h-5 w-5" />}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-semibold">{user.username}</h4>
                                            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                                                {user.role === 'admin' ? 'Admin' : 'User'}
                                            </Badge>
                                            {user.id === currentUser?.id && (
                                                <Badge variant="outline" className="text-xs">Anda</Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Dibuat: {user.created_at ? formatDate(user.created_at) : 'N/A'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openPasswordDialog(user)}
                                        title="Ubah Password"
                                    >
                                        <Key className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEditDialog(user)}
                                        disabled={user.id === currentUser?.id}
                                        title="Edit Role"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => handleDeleteUser(user)}
                                        disabled={user.id === currentUser?.id}
                                        title="Hapus User"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Create User Dialog */}
            {showCreateDialog && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-background rounded-lg shadow-lg max-w-md w-full p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Tambah User Baru</h3>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowCreateDialog(false)}
                                className="h-8 w-8"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium">Username</label>
                                <input
                                    type="text"
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                                    placeholder="Masukkan username"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium">Password</label>
                                <input
                                    type="password"
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                                    placeholder="Masukkan password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium">Role</label>
                                <select
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                >
                                    <option value="user">User (Akses Terbatas)</option>
                                    <option value="admin">Admin (Akses Penuh)</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowCreateDialog(false)}
                                className="flex-1"
                            >
                                Batal
                            </Button>
                            <Button onClick={handleCreateUser} className="flex-1">
                                <Save className="h-4 w-4 mr-2" />
                                Simpan
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Edit Role Dialog */}
            {showEditDialog && selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-background rounded-lg shadow-lg max-w-md w-full p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Edit Role User</h3>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowEditDialog(false)}
                                className="h-8 w-8"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Username</label>
                                <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                                    {selectedUser.username}
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium">Role</label>
                                <select
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                >
                                    <option value="user">User (Akses Terbatas)</option>
                                    <option value="admin">Admin (Akses Penuh)</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowEditDialog(false)}
                                className="flex-1"
                            >
                                Batal
                            </Button>
                            <Button onClick={handleUpdateRole} className="flex-1">
                                <Save className="h-4 w-4 mr-2" />
                                Simpan
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Change Password Dialog */}
            {showPasswordDialog && selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-background rounded-lg shadow-lg max-w-md w-full p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Ubah Password</h3>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowPasswordDialog(false)}
                                className="h-8 w-8"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Username</label>
                                <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                                    {selectedUser.username}
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium">Password Baru</label>
                                <input
                                    type="password"
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-1"
                                    placeholder="Masukkan password baru"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowPasswordDialog(false)}
                                className="flex-1"
                            >
                                Batal
                            </Button>
                            <Button onClick={handleChangePassword} className="flex-1">
                                <Key className="h-4 w-4 mr-2" />
                                Ubah Password
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </motion.div>
    );
}
